'use client';

import './styles/default.scss';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useWindowVirtualizer } from '@tanstack/react-virtual';
import type { GameViewModel } from '@/types/games/game-view-model';
import { GameFactoryClient } from '@/app/components/game/game-factory.client';
import { useStore } from '@/store/store';
import { classes } from '@/utils/classes/classes';
import { useGlobalContext } from '@/store/contexts/global-context';
import { PRODUCT_THROTTLES } from '@/utils/feature-flags/constants';
import { PRISMIC_ZONE_LAYOUT } from '@/app/constants/zone';

type Props = {
    games: GameViewModel[];
    isDesktopClient?: boolean;
};

// viewport breakpoint between narrow (mobile-ish) and wide layouts
const NARROW_VIEWPORT_BREAKPOINT_PX = 768;

const useIsNarrow = (breakpoint = NARROW_VIEWPORT_BREAKPOINT_PX) => {
    const [isNarrow, setIsNarrow] = useState(false);

    useEffect(() => {
        const onResize = () => {
            setIsNarrow(window.innerWidth < breakpoint);
        };

        onResize();
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [breakpoint]);

    return isNarrow;
};

const useColumnCount = (ref: React.RefObject<HTMLElement | null>, cardWidth: number, gap: number) => {
    const [columns, setColumns] = useState(1);

    useLayoutEffect(() => {
        if (!ref.current) {
            return undefined;
        }

        const calcColumns = (width: number) => {
            const available = width;
            const perCol = cardWidth + gap;
            const cols = Math.max(1, Math.floor((available + gap) / perCol));
            setColumns(cols);
        };

        calcColumns(ref.current.clientWidth);

        const observer = new ResizeObserver(([entry]) => {
            calcColumns(entry.contentRect.width);
        });

        observer.observe(ref.current);

        return () => observer.disconnect();
    }, [ref, cardWidth, gap]);

    return columns;
};

export const VirtualGamesGrid = ({ games, isDesktopClient = false }: Props) => {
    const { featureFlags } = useGlobalContext();
    const hasCategoryV2Layout = featureFlags[PRODUCT_THROTTLES.CATEGORY_V2_LAYOUT];
    const isNarrow = useIsNarrow(768);
    const isLoggedIn = useStore((state) => state.userContext.isLoggedIn);
    const isEmbeddedGameOpen = useStore((state) => state.embeddedGame.isOpen);

    const v2CardWidth = isNarrow ? 109 : 135;
    const v2CardHeight = isNarrow ? 156 : 202;
    const defaultCardWidth = isNarrow ? 130 : 195;
    const defaultCardHeight = isNarrow ? 226 : 310;
    const cardWidth = hasCategoryV2Layout ? v2CardWidth : defaultCardWidth;
    const cardHeight = hasCategoryV2Layout ? v2CardHeight : defaultCardHeight;
    const rowGap = hasCategoryV2Layout ? 8 : 29;
    const defaultColumnGap = isNarrow ? 20 : 25;
    const columnGap = hasCategoryV2Layout ? 8 : defaultColumnGap;

    const listRef = useRef<HTMLDivElement | null>(null);
    const measuredColumns = useColumnCount(listRef, cardWidth, columnGap);
    const columns = hasCategoryV2Layout && isNarrow ? 3 : measuredColumns;
    const rowCount = Math.ceil(games.length / columns);
    const virtualRowCount = isEmbeddedGameOpen ? 0 : rowCount;

    const virtualizer = useWindowVirtualizer({
        count: virtualRowCount,
        estimateSize: () => cardHeight + rowGap,
        overscan: isEmbeddedGameOpen ? 0 : 5,
        scrollMargin: listRef.current?.offsetTop ?? 0
    });

    const virtualRows = virtualizer.getVirtualItems();
    const scrollMargin = virtualizer.options.scrollMargin ?? 0;

    const v2GridTemplateColumns = isNarrow
        ? `repeat(${columns}, minmax(0, 1fr))`
        : `repeat(${columns}, minmax(0, ${cardWidth}px))`;

    const gridLayout = hasCategoryV2Layout ? PRISMIC_ZONE_LAYOUT.ONE_ROW : PRISMIC_ZONE_LAYOUT.DEFAULT;
    const shouldHideGameInfo = !isDesktopClient && hasCategoryV2Layout && isLoggedIn;

    return (
        <div
            ref={listRef}
            className={classes('virtual-games-grid', hasCategoryV2Layout && 'virtual-games-grid--v2')}
            style={{ paddingTop: 15 }}>
            <div
                className="virtual-games-grid__container"
                style={{
                    height: virtualizer.getTotalSize()
                }}>
                {virtualRows.map((virtualRow) => {
                    const rowIndex = virtualRow.index;
                    const startIndex = rowIndex * columns;
                    const endIndex = Math.min(startIndex + columns, games.length);

                    if (startIndex >= games.length) {
                        return null;
                    }

                    return (
                        <div
                            key={virtualRow.key}
                            className="virtual-games-grid__row"
                            data-index={virtualRow.index}
                            ref={virtualizer.measureElement}
                            style={{
                                transform: `translateY(${virtualRow.start - scrollMargin}px)`,
                                display: hasCategoryV2Layout ? 'grid' : 'flex',
                                gridTemplateColumns: hasCategoryV2Layout ? v2GridTemplateColumns : undefined,
                                justifyContent: hasCategoryV2Layout ? undefined : isNarrow ? 'center' : 'flex-start',
                                columnGap,
                                paddingBottom: rowGap
                            }}>
                            {Array.from({ length: endIndex - startIndex }, (_, colOffset) => {
                                const index = startIndex + colOffset;
                                const game = games[index];

                                return (
                                    <div
                                        key={game.uid}
                                        className="virtual-games-grid__card"
                                        style={{
                                            width: hasCategoryV2Layout ? 'auto' : cardWidth,
                                            height: hasCategoryV2Layout ? 'auto' : cardHeight
                                        }}>
                                        <GameFactoryClient
                                            game={game}
                                            layout={gridLayout}
                                            priority={index === 0}
                                            {...(isDesktopClient
                                                ? { forceShowGameInfo: true, hasFavouriteButton: false }
                                                : { hideGameInfo: shouldHideGameInfo, hasFavouriteButton: true })}
                                            tracking={{
                                                gamePosition: index
                                            }}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
